/**
 * Pure view-model helpers of the menu screens, shared by BOTH shells (web Preact screens in apps/client
 * src/ui/screens, native RN screens in apps/mobile src/ui/screens/menus): channel guide clock, character-select grid,
 * versus cards, the Results reel / box score / coach tip / labels, the replay clock, settings name + rebinding copy.
 * No UI framework, no DOM / react-native: unit-tested under Node (apps/mobile src/ui/screens/menus/__tests__).
 */
import { Btn, CHARACTER_IDS, type ButtonName, type CharacterId } from '@hf/shared';
import { TOUCH_LEFT, TOUCH_RIGHT } from '../input/touch';
import { DEVICE_ID, padIndexOf } from '../input/types';
import type { PendingMatch } from './flow';
import { rankTier, teamLabel, thousands, venueChyron } from './format';
import { highlights, playTitle, topHighlights, type Highlight } from './moments';
import type { LessonId } from './tutorial/catalog';
import type { MatchResultsView, MatchSetup, PlayerResultLine, ReplayClipInfo } from './types';

// ── Shell ─────────────────────────────────────────────────────────────────────────────────────────────────

/** Cycle through options (web Shell.tsx `cycle`). */
export const cycle = <T>(list: readonly T[], cur: T, dir: -1 | 1, wrap = true): T => {
  const i = list.indexOf(cur);
  let n = (i < 0 ? 0 : i) + dir;
  if (wrap) n = (n + list.length) % list.length;
  else n = Math.min(list.length - 1, Math.max(0, n));
  return list[n]!;
};

// ── Main menu ─────────────────────────────────────────────────────────────────────────────────────────────

const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
/** Channel guide kicker: "FRI 1:17 AM  ·  ALL GAMES LIVE". */
export const guideTime = (d: Date): string => {
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${DAYS[d.getDay()]} ${((h + 11) % 12) + 1}:${m} ${h < 12 ? 'AM' : 'PM'}  ·  ALL GAMES LIVE`;
};

/**
 * Can LOCAL VERSUS be played on this device? client-core's `buildOfflineSetup` binds P1 = left keyboard half + pad 0
 * and P2 = right half + pad 1 (no touch), so a phone needs BOTH pads 0 and 1 connected, or a hardware keyboard (seen
 * as the last active device). The web preview always has a keyboard.
 */
export const localVersusReady = (o: {
  web: boolean;
  /** Ids of the CONNECTED devices. */
  connected: readonly string[];
  lastActive: string | null;
}): boolean => {
  if (o.web) return true;
  if (o.lastActive?.startsWith('kb:')) return true;
  const pads = new Set(o.connected.map(padIndexOf));
  return pads.has(0) && pads.has(1);
};

/**
 * The menu input style on this device. client-core's `inputStyle` boots as 'kb' and only follows a REAL input (the
 * shell flips it to 'touch' after the first touch), so a phone's first screens would read PRESS START with keyboard
 * keycaps. Native, before any key press: no pad = touch (TAP TO START), a pad connected = its buttons.
 */
export const menuStyleFor = (o: {
  style: 'kb' | 'pad' | 'touch';
  web: boolean;
  connected: readonly string[];
  lastActive: string | null;
}): 'kb' | 'pad' | 'touch' => {
  if (o.style !== 'kb' || o.web || o.lastActive?.startsWith('kb:')) return o.style;
  return o.connected.some((id) => padIndexOf(id) >= 0) ? 'pad' : 'touch';
};

/** Connected pads among the two LOCAL VERSUS slots (0..2). */
export const versusPadCount = (connected: readonly string[]): number => {
  const pads = new Set(connected.map(padIndexOf));
  return (pads.has(0) ? 1 : 0) + (pads.has(1) ? 1 : 0);
};

// ── Character select ──────────────────────────────────────────────────────────────────────────────────────

export const RANDOM_CELL = 4;
/** Grid cell → character (cells 0..3 in reading order; 4 = RANDOM HEAD). */
export const CELL_CHARACTERS: readonly CharacterId[] = ['rookie', 'bigtex', 'zorg', 'grandpa'];

/** Cursor movement on the 2×2 grid + RANDOM bar below it. */
export const moveCursor = (cur: number, dir: 'up' | 'down' | 'left' | 'right'): number => {
  if (cur === RANDOM_CELL) return dir === 'up' ? 2 : cur;
  if (dir === 'left' || dir === 'right') return cur ^ 1;
  if (dir === 'up') return cur >= 2 ? cur - 2 : cur;
  return cur < 2 ? cur + 2 : RANDOM_CELL;
};

export const DIFFICULTY_BLURB: Record<number, string> = {
  0: 'Learning the ropes. Bites on every fake.',
  1: 'Plays it straight. Punishes lazy jumps.',
  2: 'Reads the counter square. Fakes you back.',
  3: 'Hall-of-fame reads. Reacts, never guesses.',
};

// ── Versus ────────────────────────────────────────────────────────────────────────────────────────────────

const KIND_NAME: Record<string, string> = { quick: 'QUICK MATCH', ranked: 'RANKED', private: 'PRIVATE ROOM' };

/** An online match's Versus card (YOU / OPPONENT tags, ranked tiers + Elo on both sides). */
export const pendingFromOnline = (setup: MatchSetup, myRating?: number): PendingMatch => {
  const kind = setup.online?.kind ?? 'quick';
  const you = setup.online?.you ?? 0;
  const tag = (i: 0 | 1): string => {
    const slot = setup.slots[i];
    const who = i === you ? 'YOU' : 'OPPONENT';
    // Ranked: both cards carry tier + Elo (QA r4: only YOU had one). Ours prefers the live lobby rating.
    const elo = i === you ? (myRating ?? setup.online?.ratings?.[i]) : setup.online?.ratings?.[i];
    const rating =
      elo !== undefined && kind === 'ranked' ? ` · ${rankTier(elo)} · ELO ${thousands(elo)}` : '';
    return `${who} · ${slot.name.toUpperCase()}${rating}`;
  };
  return {
    setup,
    venue: venueChyron(setup.config, `ONLINE · ${KIND_NAME[kind] ?? 'ONLINE'}`),
    tags: [tag(0), tag(1)],
  };
};

// ── Results ───────────────────────────────────────────────────────────────────────────────────────────────

/** Results ignore device confirm / back this long after they appear (the final whistle comes mid-mash). */
export const RESULTS_ARM_MS = 1000;
export const REEL_SIZE = 3;

/** Reel cards: the recorded clips (playable) when there are any, else the match's big moments (static cards). */
export interface ReelCard {
  key: string;
  text: string;
  sub?: string;
  tone: string;
  character?: string;
  expression?: string;
  side?: 0 | 1;
  atSec: number;
  /** Index into the playable clips, or -1 for a static card. */
  clip: number;
}

export const reelCards = (
  clips: readonly ReplayClipInfo[],
  playable: boolean,
  moments: readonly Highlight[] = highlights.peek(),
): ReelCard[] =>
  playable && clips.length > 0
    ? clips.slice(0, REEL_SIZE).map((c, i) => ({
        key: `c${c.id}`,
        text: playTitle(c.title),
        sub: c.sub,
        tone: c.tone,
        character: c.character,
        expression: c.expression,
        side: c.side,
        atSec: c.matchSec,
        clip: i,
      }))
    : topHighlights(moments, REEL_SIZE).map((h, i) => ({
        key: `h${i}`,
        text: playTitle(h.text),
        ...(h.sub ? { sub: h.sub } : {}),
        tone: h.tone,
        ...(h.character ? { character: h.character } : {}),
        ...(h.expression ? { expression: h.expression } : {}),
        ...(h.side !== undefined ? { side: h.side } : {}),
        atSec: h.atSec,
        clip: -1,
      }));

/** Reel card caption: every card says what happened ("ZORG · SACK"). */
export const reelCaption = (text: string, sub: string | undefined): string => {
  const play = text.replace(/!+$/, '').trim();
  const s = (sub ?? '').trim();
  if (!s) return play;
  if (s.includes('·') || s.split(/\s+/).length > 3) return s;
  // "GRANDPA GRIT · SACKED" read as Grandpa being sacked: the named head MADE the sack
  if (/^SACK/.test(play)) return `SACK BY ${s}`;
  return `${s} · ${play}`;
};

/** Play type of a reel card (thumbnail styling): score / takeaway / hit / fake. */
export const reelKind = (text: string): 'k-score' | 'k-pick' | 'k-hit' | 'k-fake' | 'k-play' =>
  /TOUCHDOWN|PICK SIX|FIELD GOAL|SAFETY|2-POINT|EXTRA POINT/.test(text)
    ? 'k-score'
    : /PICKED|INTERCEPT|RECOVERED/.test(text)
      ? 'k-pick'
      : /SACK|TRUCK|BULLDOZ|STUFF/.test(text)
        ? 'k-hit'
        : /FAKE|SPUN|HURDLE|MIRAGE/.test(text)
          ? 'k-fake'
          : 'k-play';

/** The one local human of the match (vs CPU, online), else -1. */
export const soloSide = (setup: Pick<MatchSetup, 'slots'> | null | undefined): 0 | 1 | -1 => {
  if (!setup) return -1;
  const h0 = setup.slots[0].kind === 'human';
  const h1 = setup.slots[1].kind === 'human';
  return h0 === h1 ? -1 : h0 ? 0 : 1;
};

/** Final-score / box-score labels (head abbreviations, or slot tags in a mirror match). */
export const resultLabels = (
  r: Pick<MatchResultsView, 'lines'>,
  setup: Pick<MatchSetup, 'slots' | 'online'> | null | undefined,
): [string, string] => {
  const idx = (c: CharacterId): number => Math.max(0, CHARACTER_IDS.indexOf(c));
  const chars = [idx(r.lines[0].character), idx(r.lines[1].character)];
  return [teamLabel(chars, setup, 0), teamLabel(chars, setup, 1)];
};

/** A coaching line after a loss, picked from the box score, with the Football 101 lesson that fixes it. */
export interface CoachTip {
  title: string;
  /** Text with button tokens: "{D}" renders the player's key for that logical button. */
  body: string;
  lesson: LessonId;
}

export const coachTip = (r: MatchResultsView, you: 0 | 1): CoachTip | null => {
  if (r.winner === -1 || r.winner === you) return null;
  const me = r.lines[you];
  const them = r.lines[you === 0 ? 1 : 0];
  const times = (n: number): string => (n === 1 ? 'ONCE' : n === 2 ? 'TWICE' : `${n} TIMES`);
  if (them.sacks >= 2)
    return {
      title: `SACKED ${times(them.sacks)}`,
      body: 'Read his move: tap {U} to HURDLE a dive, hold {D} to TRUCK through a leap.',
      lesson: 'counters',
    };
  if (them.interceptions >= 1)
    return {
      title: `PICKED OFF ${times(them.interceptions)}`,
      body: 'Throw into open grass away from his head, then chase your own pass down.',
      lesson: 'pass',
    };
  if (them.touchdowns >= 2 && me.tackles <= them.touchdowns)
    return {
      title: 'THEY RAN RIGHT PAST YOU',
      body: 'On defense, wait for GO, stay in front of the runner, then {A} DIVE to tackle.',
      lesson: 'defense',
    };
  if (me.yards + me.passYards < 25)
    return {
      title: 'STUCK AT THE LINE',
      body: 'Fake him out: a feint makes the defender commit, then you go around him.',
      lesson: 'fakes',
    };
  return {
    title: 'SO CLOSE!',
    body: 'Big plays and fakes fill your POWER. When it is full, press {S} for your special.',
    lesson: 'special',
  };
};

/** MVP stat line: "3 TD · 2 FEINTS BAITED · 214 YDS". */
export const mvpLine = (l: PlayerResultLine): string => {
  const parts: string[] = [];
  if (l.touchdowns) parts.push(`${l.touchdowns} TD`);
  if (l.baits) parts.push(`${l.baits} FEINT${l.baits === 1 ? '' : 'S'} BAITED`);
  if (l.interceptions) parts.push(`${l.interceptions} PICK${l.interceptions === 1 ? '' : 'S'}`);
  if (l.sacks) parts.push(`${l.sacks} SACK${l.sacks === 1 ? '' : 'S'}`);
  const yds = (Number(l.yards) || 0) + (Number(l.passYards) || 0);
  if (yds) parts.push(`${yds} YDS`);
  if (parts.length < 2 && l.tackles) parts.push(`${l.tackles} TACKLE${l.tackles === 1 ? '' : 'S'}`);
  return parts.slice(0, 3).join(' · ') || 'SHOWED UP AND COMPETED';
};

/** Box-score cell: a missing / non-finite stat shows 0 (never "undefined" / "NaN"). */
export const statNum = (v: number | undefined): string =>
  typeof v === 'number' && Number.isFinite(v) ? String(Math.round(v)) : '0';

export const BOX_ROWS: readonly [string, (l: PlayerResultLine) => string][] = [
  ['PASSING YDS', (l) => statNum(l.passYards)],
  ['RUSHING YDS', (l) => statNum(l.yards)],
  ['TOUCHDOWNS', (l) => statNum(l.touchdowns)],
  ['FEINTS BAITED', (l) => statNum(l.baits)],
  ['TACKLES', (l) => statNum(l.tackles)],
  ['CATCHES', (l) => statNum(l.catches)],
  ['SACKS / PICKS', (l) => `${statNum(l.sacks)} / ${statNum(l.interceptions)}`],
];

/** GAME MVP with the trophy (winner or draw), STAR OF THE GAME without one when the best player lost. */
export const mvpBadge = (r: MatchResultsView): { label: string; trophy: boolean } =>
  r.winner !== -1 && r.winner !== r.mvp
    ? { label: 'STAR OF THE GAME', trophy: false }
    : { label: 'GAME MVP', trophy: true };

/** Online rematch button copy. */
export const rematchLabel = (
  isOnline: boolean,
  rematch: { you: boolean; opponent: boolean; declined: boolean } | null,
): string =>
  !isOnline
    ? 'REMATCH'
    : rematch?.declined
      ? 'REMATCH DECLINED'
      : rematch?.you
        ? 'WAITING…'
        : rematch?.opponent
          ? 'ACCEPT REMATCH'
          : 'REMATCH';

/** Where the exit really goes: online results return to the lobby (a private room keeps you in the room). */
export const leaveLabelFor = (setup: MatchSetup | null): string =>
  setup?.online ? (setup.online.kind === 'private' ? 'BACK TO ROOM' : 'BACK TO LOBBY') : 'MAIN MENU';

/** The result from the local player's side: VICTORY / DEFEAT (one local human), else who won. */
export const statusLine = (r: MatchResultsView, you: 0 | 1 | -1): { big: string | null; text: string } => {
  const score = (w: 0 | 1): string => `${r.lines[w].score}–${r.lines[w === 0 ? 1 : 0].score}`;
  if (r.status === 'disputed') return { big: null, text: 'RESULT DISPUTED · NO RATING CHANGE' };
  if (r.winner === -1)
    return { big: null, text: r.status === 'forfeit' ? 'OPPONENT FORFEITED' : 'NO RESULT' };
  const won = r.winner === you;
  const forfeit = r.status === 'forfeit';
  if (you !== -1)
    return won
      ? { big: 'VICTORY!', text: forfeit ? 'OPPONENT FORFEITED · YOU WIN' : `YOU WIN ${score(r.winner)}` }
      : { big: 'DEFEAT', text: `${r.lines[r.winner].name.toUpperCase()} WINS ${score(r.winner)}` };
  return { big: null, text: `${r.lines[r.winner].name.toUpperCase()} WINS ${score(r.winner)}` };
};

/** The device whose keys a tip should show for `slot` (web hud/common.tsx `promptDevice`). */
export const promptDevice = (
  setup: MatchSetup | null,
  slot: 0 | 1,
  lastActive: string | null,
): string | null => {
  const s = setup?.slots[slot];
  if (!s || s.kind !== 'human') return null;
  const humans = setup.slots.filter((x) => x.kind === 'human').length;
  const ids = s.deviceIds ?? [humans === 1 ? 'kb:solo' : slot === 0 ? 'kb:p1' : 'kb:p2'];
  if (lastActive) {
    if (ids.includes(lastActive)) return lastActive;
    if (padIndexOf(lastActive) >= 0 && ids.includes(DEVICE_ID.ANY_PAD)) {
      const other = setup.slots[slot === 0 ? 1 : 0];
      const taken = other?.kind === 'human' && (other.deviceIds ?? []).includes(lastActive);
      if (!taken) return lastActive;
    }
  }
  return ids.find((d) => d !== DEVICE_ID.ANY_PAD) ?? ids[0] ?? null;
};

/** The on-screen pad's label for a logical button ("JUMP", "▼", "ACTION"), or null when the pad has none. */
export const touchLabel = (button: ButtonName): string | null => {
  const bits = Btn[button as keyof typeof Btn];
  const spec = [...TOUCH_LEFT, ...TOUCH_RIGHT].find((s) => s.bits === bits);
  return spec ? spec.label : null;
};

/** `m:ss` tape counter. */
export const fmtTime = (s: number): string => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

// ── Settings ──────────────────────────────────────────────────────────────────────────────────────────────

/** Player-name sanitizer matching the server's PlayerNameSchema (letters, numbers, space _ . - ! ?; max 16). */
export const sanitizeName = (raw: string): string =>
  raw
    .replace(/[^\p{L}\p{N} _.\-!?]/gu, '')
    .replace(/\s+/g, ' ')
    .slice(0, 16);

/** The device the Controls panel opens on: the one the player is holding. */
export const initialDeviceIndex = (devices: readonly { id: string }[], lastActive: string | null): number => {
  const i = lastActive ? devices.findIndex((d) => d.id === lastActive) : -1;
  return i >= 0 ? i : 0;
};

/** Footer legend while a row captures (a B tap BINDS B on a pad: only a hold or Start cancels). */
export const captureHints = (mode: 'pad' | 'kb'): [string, string][] =>
  mode === 'pad'
    ? [
        ['ANY BUTTON', 'BIND'],
        ['HOLD B|START', 'CANCEL'],
      ]
    : [
        // a keyboard capture: Esc (or any pad's B / Start, see `usePadCancel`) — never the routed (B) glyph alone
        ['ANY KEY', 'BIND'],
        ['ESC|B', 'CANCEL'],
      ];

/** Rebind toast: the new key, plus what happened to the button that owned it. */
export const rebindToast = (
  rowName: (b: ButtonName) => string,
  button: ButtonName,
  code: string,
  moved: { button: ButtonName; now: string | null; unbound: boolean } | null,
  label: (code: string) => string,
): string => {
  const head = `${rowName(button)} → ${label(code)}`;
  if (!moved) return head;
  const other = rowName(moved.button);
  if (moved.now !== null) return `${head} · ${other} now ${label(moved.now)}`;
  if (moved.unbound) return `${head} · ${other} is unbound`;
  return `${head} · taken from ${other}`;
};

export const SHAKE_STEPS = [0, 0.5, 1] as const;
export const shakeName = (v: number): string => (v <= 0 ? 'OFF' : v < 0.75 ? 'LOW' : 'FULL');
/** Nearest SHAKE_STEPS entry of a saved value. */
export const shakeStep = (v: number): (typeof SHAKE_STEPS)[number] =>
  SHAKE_STEPS.reduce(
    (best, x) => (Math.abs(x - v) < Math.abs(best - v) ? x : best),
    1 as number,
  ) as (typeof SHAKE_STEPS)[number];
